import { Cartesian3 } from "cesium";
import { EarthBrainViewerAPIs } from "./EarthBrainViewerAPIs";
export class TrenchConstructionAPIs extends EarthBrainViewerAPIs {
    /*
     * Returns length of the trench
     * @param trenchID
     */
    getLengthOfTrench(trenchID) {
        return this._scDesign.getLengthOfTrench(trenchID);
    }
    getControlPointIndexById(trenchId, pointId) {
        return this._scDesign.getControlPointIndexById(trenchId, pointId);
    }
    isTrenchLoaded(trenchId) {
        return this._scDesign.isTrenchLoaded(trenchId);
    }
    getTrenchPointLength(trenchID) {
        return this._scDesign.getTrenchPointLength(trenchID);
    }
    getTrenchPointIDFromIndex(trenchID, index) {
        return this._scDesign.getTrenchPointIDFromIndex(trenchID, index);
    }
    /**
     * Returns the object containing minimum slope value and maximum slope value of the trench
     * @param trenchID
     * @returns
     */
    getTrenchSlopeInfo(trenchID) {
        return this._scDesign.getTrenchSlopeInfo(trenchID);
    }
    /**
     * Returns the object containing minimum elevation and maximum elevation of the trench
     * @param trenchID
     * @returns
     */
    getTrenchHeightInfo(trenchID) {
        return this._scDesign.getTrenchHeightInfo(trenchID);
    }
    getTrenchMeshGeometry(trenchId) {
        return this._scDesign.getTrenchMeshGeometry(trenchId);
    }
    getTrenchPolylineVolumeGeometry(trenchId) {
        return this._scDesign.getTrenchPolylineVolumeGeometry(trenchId);
    }
    /**
     * Function returns Control point coordinates
     * @param trenchID Flat ground ID
     * @param controlPointId Flat ground Control point ID whose coordinates we require
     * @returns Cesium.Cartesian3 values of control point
     */
    getCtrlPointCordiOnTrench(trenchID, controlPointId) {
        return this._scDesign.getCtrlPointCordiOnTrench(trenchID, controlPointId);
    }
    /**
     * This function will calculate volume of Trench
     * @param trenchID ID of Trench whose volume to calculate
     * @param cellX Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellY Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellZ Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @returns Fill and Cut Volume in cu. meter.
     */
    async volumeCalculationForTrench(trenchID) {
        return await this._scDesign.volumeCalculationForTrench(trenchID);
    }
    /**
     * Saves Trench object as JSON string and return that string
     * @param trenchID
     * @returns
     */
    getTrenchJSON(trenchID) {
        return this._scDesign.getTrenchJSON(trenchID);
    }
    /**
     * Returns Trench Linear data as JSON string and return that string
     * @param trenchInfo contains params required for generating Trench Linear Data.
     * @returns
     */
    getTrenchLinearData(trenchInfo) {
        return this._scDesign.getTrenchLinearData(trenchInfo);
    }
    //Creation
    /**
     * Creates Trench using json string and return trench id
     * @param jsonString
     * @returns
     */
    async loadTrenchJSON(data, stationInterval, updateInListCallback) {
        return this._scDesign.loadTrenchJSON(data, stationInterval, updateInListCallback);
    }
    /**
     * Enable Trench Creation
     * @param callback Callback function to be called when trench creation is finished
     * @param associatedTerrain Associated terrain for the trench (id and terrain type)
     * @param associatedRubbingDestination Associated rubbing destination for the trench (target and id)
     */
    enableTrenchCreation(callback, associatedTerrain, associatedRubbingDestination) {
        this._scDesign.enableTrenchCreation(callback, associatedTerrain, associatedRubbingDestination);
    }
    /**
     * Add Control Point on Trench before the control point on which this function is called
     * @param trenchID ID Of the trench to update the settings
     *
     */
    addControlPointOnTrench(trenchID, controlPointId) {
        this._scDesign.addControlPointOnTrench(trenchID, controlPointId);
    }
    /**
     * Add Control Point on Trench Segment
     * @param trenchId ID of the trench
     * @param segmentId ID of the segment
     * @param point Point on the segment in local space
     */
    addControlPointOnTrenchSegment(trenchId, segmentId, point) {
        this._scDesign.addControlPointOnTrenchSegment(trenchId, segmentId, Cartesian3.unpack(point));
    }
    /**
     * This Function will let you add a new control point on the given point on the center line
     * @param trenchID unique ID of the trench
     * @param localPoint point on center line of the trench in local space
     */
    addControlPointOnCenterLineOfTrench(trenchId, localPoint) {
        this._scDesign.addControlPointOnCenterLineOfTrench(trenchId, Cartesian3.unpack(localPoint));
    }
    /**
     * Trench Extension function will extend the trench by one control point.
     * This function can also be called by pressing 'Shift + T' on keyboard in edit mode.
     * @param trenchID unique ID of the trench
     */
    // public trenchExtension(trenchID: string) {
    // 	this._scDesign.trenchExtension(trenchID);
    // }
    /**
     * This Function will check whether there is any trench under creation.
     * @returns True or False for trench under creation or not.
     */
    checkTrenchUnderCreation() {
        return this._scDesign.checkTrenchUnderCreation();
    }
    //Edit
    enableTrenchEdit(status, trenchID, updateCallback) {
        this._scDesign.enableTrenchEdit(status, trenchID, updateCallback);
    }
    updateTrenchHorizontalAlignmentType(trenchId, horizontalAlignmentType) {
        this._scDesign.updateTrenchHorizontalAlignmentType(trenchId, horizontalAlignmentType);
    }
    /**
     * Function to updates coordinates values of control points
     * @param trenchID Trench ID
     * @param controlPointID Trench Control point ID whose coordinates we will update
     * @param x by this x value we need to update control point value
     * @param y by this y value we need to update control point value
     * @param z by this z value we need to update control point value
     */
    updateCtrlPointCordiOnTrench(trenchID, controlPointID, x, y, z) {
        this._scDesign.updateCtrlPointCordiOnTrench(trenchID, controlPointID, new Cartesian3(x, y, z));
    }
    /**
     * Update the settings for existing trench
     * @param trenchID ID Of the trench to update the settings
     * @param settings setting's object
     * @param rubbingDestination Optional associated rubbing destination for the trench (target and id)
     */
    async updateTrenchSettings(trenchID, settings, rubbingDestination) {
        await this._scDesign.updateTrenchSettings(trenchID, settings, rubbingDestination);
    }
    // public async restoreTrenchAfterHiding(trenchID: string): Promise<void> {
    // 	await this._scDesign.restoreTrenchAfterHiding(trenchID);
    // }
    updateTrenchMaterialSettings(trenchID, commonMaterial, cutMaterial) {
        this._scDesign.updateTrenchMaterialSettings(trenchID, commonMaterial, cutMaterial);
    }
    /**
     * This Function will finish Trench Creation.
     */
    finishTrenchCreation() {
        this._scDesign.finishTrenchCreation();
    }
    cancelTrenchCreation() {
        this._scDesign.cancelTrenchCreation();
    }
    /**
     * Deletes the trench mesh, control points, control lines and polyline attached with trench
     * @param trenchID unique ID of the trench
     */
    deleteTrench(trenchID) {
        this._scDesign.deleteTrench(trenchID);
    }
    // trench edit
    removeControlPointOnTrench(controlPointId) {
        this._scDesign.removeControlPointOnTrench(controlPointId);
    }
    // public removeSelectedSlopeBreak(trenchID: string) {
    //  this._scDesign.removeSelectedSlopeBreak(trenchID);
    // }
    /**
     * Force-recalculate trench geom
     * @param trenchId
     */
    // public recalculateTrenchGeometry(trenchId: string) {
    //   this._scDesign.recalculateTrenchGeometry(trenchId);
    // }
    /**
     * Is Trench Control Point Removable
     * @param trenchId Id of trench
     * @param controlPointId Id of control Point
     * @returns Returns true if trench control point is removable
     */
    isTrenchControlPointRemovable(trenchId, controlPointId) {
        return this._scDesign.isTrenchControlPointRemovable(trenchId, controlPointId);
    }
    /**
     * Function to update the associated point cloud of a trench (point cloud needs to loaded before calling this function)
     * @param id Id of the trench whose associated point cloud needs to be updated
     * @param pointCloudId Id of the new associated point cloud
     */
    // public updatePointCloudAssociatedWithTrench(trenchId: string, pointCloudId: number) {
    //   this._scDesign.updatePointCloudAssociatedWithObject(trenchId, pointCloudId, "trench");
    // }
    getReferencePointElevationOfTrench(id) {
        return this._scDesign.getReferencePointElevationOfTrench(id);
    }
    getAverageHeightOfFirstAndLastPointOfTrench(id) {
        return this._scDesign.getAverageHeightOfFirstAndLastPointOfTrench(id);
    }
    getSegmentIdByPointForTrench(trenchId, point) {
        return this._scDesign.getSegmentByPointForTrench(trenchId, Cartesian3.fromArray(point))?.id;
    }
    setTrenchCenterLineVisibility(trenchID, visible) {
        this._scDesign.setTrenchCenterLineVisibility(trenchID, visible);
    }
    setTrenchAlignmentVisibility(trenchID, visible) {
        this._scDesign.setTrenchAlignmentVisibility(trenchID, visible);
    }
    setTrenchIpLineVisibility(trenchID, visible) {
        this._scDesign.setTrenchIpLineVisibility(trenchID, visible);
    }
    setTrenchSurfaceVisibility(trenchID, visible) {
        this._scDesign.setTrenchSurfaceVisibility(trenchID, visible);
    }
    setTrenchEmbankmentVisibility(trenchID, visible) {
        this._scDesign.setTrenchEmbankmentVisibility(trenchID, visible);
    }
}
//# sourceMappingURL=trench-construction.js.map